import { ChangeEventHandler } from 'react';
import { UseControllerProps } from 'react-hook-form';

import { Country } from '@xemplo/country-helper';
import { XemploInputProps } from '@xemplo/xemplo-input';

export const RuntimeFlagMode = {
  Sprite: 'sprite',
  Individual: 'individual',
  None: 'none',
} as const;
export type RuntimeFlagMode = (typeof RuntimeFlagMode)[keyof typeof RuntimeFlagMode];

/**
 * Defines the modes for displaying country flags in the PhoneField component.
 */
export const PhoneFieldFlagMode = {
  ...RuntimeFlagMode,
  Auto: 'auto',
} as const;
export type PhoneFieldFlagMode =
  (typeof PhoneFieldFlagMode)[keyof typeof PhoneFieldFlagMode];

/**
 * The type definition for the flags-related
 * properties of the PhoneField component.
 */
export type FlagProps = {
  /**
   * The default country code to be pre-selected.
   * As an UX requirement, we always have a pre-selected country.
   *
   * @default Country.Code.Australia
   * @see {@link Country.Code}
   */
  defaultCountryCode?: Country.Code;

  /**
   * List of country codes to be highlighted in the country selector.
   * The highlighted countries will appear at the top of the list.
   *
   * @remark only applicable if `selectableCountries` is NOT provided.
   * @see {@link Country.Code}
   */
  highlights?: Country.Code[];

  /**
   * List of country codes that can be selected by the user.
   * If not provided, all countries will be selectable.
   * If provided, no grouping will be applied.
   *
   * @default Country.getAllCountries()
   * @see {@link Country.Code}
   */
  selectableCountries?: Country.Code[];

  /**
   * Determines how country flags are displayed.
   * - 'auto': Tries to determine the most efficient way to load flags.
   * - 'sprite': Uses a single sprite image for all flags.
   * - 'individual': Loads each flag as a separate image.
   * - 'none': Does not display flags.
   *
   * @default 'auto'
   */
  flagMode?: PhoneFieldFlagMode;

  /** Event triggered when the selected country changes */
  onCountryChange?: (countryCode: Country.Code) => void;
};

export type NormalizedFlagsProps = Omit<FlagProps, 'flagMode' | 'defaultCountryCode'> & {
  /** The resolved default country code */
  defaultCountryCode: Country.Code;

  /** The resolved runtime flag mode */
  flagMode: RuntimeFlagMode;

  /** @internal calculated field */
  useSprite: boolean;

  /** Event triggered when the selected country changes */
  onCountryChange?: (countryCode: Country.Code) => void;
};

export type ValueChangeFn = (value: string) => void;

export type PhoneFieldProps = FlagProps &
  Omit<
    XemploInputProps,
    'disableDescriptionOrError' | 'leadingIcon' | 'trailingIcon' | 'onChange'
  > & {
    /**
     * The standard change event for the input element.
     *
     * NOTE: While the component sanitizes input to allow only numeric characters,
     * this onChange event receives the RAW unsanitized value from the browser
     * before any processing occurs.
     *
     * The sanitized value is then applied via the controlled component pattern,
     * so the displayed value will only show digits, but this event handler
     * receives the original input including any non-numeric characters.
     *
     * ⚠️ Use with caution - prefer `onValueChange` which provides the sanitized,
     * combined value (country code + phone number) after all processing.
     *
     * This event only captures input field changes and does NOT
     * capture country selector changes. For country changes, use:
     * - `onCountryChange` - for country selection only
     * - `onValueChange` - for combined sanitized value (recommended)
     */
    onChange?: ChangeEventHandler<HTMLInputElement>;

    /**
     * This event combines the values from the selected country
     * and input value and is emitted whenever there is a change in either.
     */
    onValueChange?: ValueChangeFn;
  };

export type PhoneFieldRhfProps = Omit<PhoneFieldProps, 'value' | 'defaultValue'> & {
  /** Validation rules for the field */
  rules?: UseControllerProps['rules'];

  /**
   * Custom error message for invalid phone numbers
   * @default 'Invalid phone number'
   */
  invalidPhoneNumberMessage?: string;
};

export type PhoneFieldRef = HTMLInputElement & {
  setSelectedCountry: (countryCode: Country.Code) => void;
};
